// client/src/pages/Dashboard.js

import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../api";
import emailjs from "@emailjs/browser";

// Icons & UI
import { 
  LayoutDashboard, Compass, Award, Mail, LogOut, 
  ChevronRight, Download, PlayCircle, ArrowLeft, CreditCard, 
  CheckCircle, BookOpen, Phone, MapPin, 
  Clock, Globe, FileText, ShieldCheck, Star, Zap,
  ArrowRight, Video, Bell, X, Lock, Unlock, Hammer, Settings, Server, Target, Layers, Users, ExternalLink
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// --- ASSETS IMPORT ---
import Logo from "../assets/logo.png"; 
import GenAIImg from "../assets/genai.png";
import FullStackImg from "../assets/fullstack.png";
import CloudImg from "../assets/cloud.png";
import CyberImg from "../assets/cyber.png";
import NoCodeImg from "../assets/nocode.png";
import AIDataImg from "../assets/aidata.png";
import DefaultImg from "../assets/back.jpg"; 
import Scene1 from "../assets/scene1.jpg"; 

// --- HELPER: Razorpay Script Loader ---
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// --- HELPER: Smart Image Mapper ---
const getCertImage = (title) => {
  if (!title) return DefaultImg;
  const t = title.toLowerCase();
  if (t.includes("generative") || t.includes("llm")) return GenAIImg;
  if (t.includes("full stack") || t.includes("web")) return FullStackImg;
  if (t.includes("cloud") || t.includes("devops")) return CloudImg;
  if (t.includes("cyber") || t.includes("security")) return CyberImg;
  if (t.includes("no-code") || t.includes("automation")) return NoCodeImg;
  if (t.includes("data") || t.includes("ai")) return AIDataImg;
  return DefaultImg;
};

// --- HELPER: Get Course Route ---
const getCourseRoute = (title) => {
  if (!title) return "/dashboard";
  const t = title.toLowerCase();
  if (t.includes("generative")) return "/course/genai";
  if (t.includes("full stack")) return "/course/fullstack";
  if (t.includes("cloud")) return "/course/cloud";
  if (t.includes("cyber")) return "/course/cyber";
  if (t.includes("no-code")) return "/course/nocode";
  if (t.includes("data")) return "/course/dataai";
  return "/course/genai"; 
};

// --- HELPER: Rich Content Mapper (Hooks, Descriptions, Skills) ---
const getCertDetails = (title) => {
  if (!title) return {
    hook: "Advance your career today.",
    colorClass: "text-slate-600",
    rating: "4.7",
    students: "800+",
    desc1: "This certification validates your core competencies in this domain. It is designed to test your knowledge against industry standards.",
    desc2: "By earning this credential, you demonstrate your commitment to professional growth and technical excellence.",
    skills: ["Core Concepts", "Best Practices", "Implementation", "Strategy"],
    links: []
  };

  const t = title.toLowerCase();
  
  if (t.includes("generative") || t.includes("llm")) return {
    hook: "Master the Frontier of Artificial Intelligence",
    colorClass: "text-indigo-600",
    rating: "4.9",
    students: "1.2k+",
    desc1: "Dive deep into the architecture of Large Language Models. This certification covers the end-to-end process of building generative AI applications, from prompt engineering and RAG pipelines to fine-tuning models for enterprise use cases.",
    desc2: "Designed for forward-thinking developers, this exam validates your ability to integrate cutting-edge AI into real-world software, ensuring you stay ahead in the rapidly evolving tech landscape.",
    skills: ["Transformers & LLMs", "RAG Pipelines", "LangChain", "Vector Databases", "Prompt Engineering"],
    links: [
        { label: "LLM Course Chapter 1", url: "https://huggingface.co/learn/llm-course/chapter1/1?referrer=grok.com" },
        { label: "Building Your Own RAG", url: "https://huggingface.co/blog/ngxson/make-your-own-rag?referrer=grok.com" },
        { label: "Vector Database Fundamentals", url: "https://www.pinecone.io/learn/vector-database/?referrer=grok.com" },
        { label: "Quantization for Transformers", url: "https://huggingface.co/blog/overview-quantization-transformers?referrer=grok.com" }
    ]
  };
  
  if (t.includes("full stack") || t.includes("web")) return {
    hook: "Architect End-to-End Modern Web Solutions",
    colorClass: "text-blue-600",
    rating: "4.8",
    students: "2.5k+",
    desc1: "Become the architect of the modern web. This certification tests your proficiency across the entire stack, including responsive frontend interfaces, robust backend APIs, and database management.",
    desc2: "You will prove your ability to deploy full-scale applications, manage state effectively, and implement secure authentication systems, making you an invaluable asset to any engineering team.",
    skills: ["React & Node.js", "Database Design", "API Architecture", "State Management", "Deployment"],
    links: [
        { label: "MDN Web Development Learn", url: "https://developer.mozilla.org/en-US/docs/Learn_web_development?referrer=grok.com" },
        { label: "React Handbook for Beginners", url: "https://www.freecodecamp.org/news/react-handbook-for-beginners-learn-jsx-hooks-rendering/?referrer=grok.com" },
        { label: "Express.js Documentation", url: "https://expressjs.com/?referrer=grok.com" },
        { label: "JWT Authentication Handbook", url: "https://www.freecodecamp.org/news/the-json-web-token-handbook-learn-to-use-jwts-for-web-authentication/?referrer=grok.com" }
    ]
  };

  if (t.includes("cloud") || t.includes("devops")) return {
    hook: "Scale Infrastructure with Confidence",
    colorClass: "text-emerald-600",
    rating: "4.9",
    students: "1.8k+",
    desc1: "Validate your expertise in cloud infrastructure and CI/CD automation. This exam focuses on containerization, orchestration with Kubernetes, and managing scalable cloud environments.",
    desc2: "Earning this certification proves you can bridge the gap between development and operations, ensuring rapid, reliable software delivery and high availability systems.",
    skills: ["AWS/Azure", "Docker & K8s", "CI/CD Pipelines", "Infrastructure as Code", "Monitoring"],
    links: [
        { label: "PaaS vs IaaS vs SaaS Guide", url: "https://cloud.google.com/learn/paas-vs-iaas-vs-saas?referrer=grok.com" },
        { label: "Azure Autoscale Overview", url: "https://learn.microsoft.com/en-us/azure/azure-monitor/autoscale/autoscale-overview?referrer=grok.com" },
        { label: "Terraform Tutorials", url: "https://developer.hashicorp.com/terraform/tutorials?referrer=grok.com" },
        { label: "IAM Data Protection", url: "https://docs.aws.amazon.com/IAM/latest/UserGuide/data-protection.html?referrer=grok.com" }
    ]
  };

  if (t.includes("cyber") || t.includes("security")) return {
    hook: "Secure the Digital Frontier",
    colorClass: "text-amber-600",
    rating: "4.9",
    students: "950+",
    desc1: "Step into the role of a digital guardian. This certification covers critical security protocols, threat analysis, and ethical hacking techniques to secure infrastructure.",
    desc2: "You will demonstrate the ability to identify vulnerabilities before they are exploited and implement robust defense mechanisms compliant with global security standards.",
    skills: ["Network Security", "Ethical Hacking", "Compliance", "Threat Analysis", "Cryptography"],
    links: [
        { label: "The CIA Triad Explained", url: "https://www.fortinet.com/resources/cyberglossary/cia-triad?referrer=grok.com" },
        { label: "Firewalls and IDS/IPS", url: "https://www.infosecinstitute.com/resources/network-security-101/firewalls-and-ids-ips/?referrer=grok.com" },
        { label: "Learning SIEM Systems", url: "https://www.splunk.com/en_us/blog/learn/siem-security-information-event-management.html?referrer=grok.com" },
        { label: "AWS IAM Introduction", url: "https://docs.aws.amazon.com/IAM/latest/UserGuide/introduction.html?referrer=grok.com" }
    ]
  };

  if (t.includes("no-code") || t.includes("automation")) return {
    hook: "Automate Workflows Without Limits",
    colorClass: "text-purple-600",
    rating: "4.7",
    students: "1.1k+",
    desc1: "Master the art of efficiency through automated systems. This certification focuses on connecting disparate platforms using no-code tools to create seamless, hands-free business processes.",
    desc2: "Demonstrate your ability to design complex logic flows and triggers that save hundreds of engineering hours, making you a vital asset for operational excellence.",
    skills: ["Zapier/Make", "API Integrations", "Workflow Mapping", "Logic Branching", "Data Syncing"],
    links: [
        { label: "No-Code Automation Guide", url: "https://zapier.com/blog/no-code-automation/?referrer=grok.com" },
        { label: "Benefits of No-Code", url: "https://blog.pluga.co/en/no-code-automation/?referrer=grok.com" },
        { label: "Power Automate Triggers", url: "https://learn.microsoft.com/en-us/power-automate/triggers-introduction?referrer=grok.com" }
    ]
  };

  if (t.includes("data") || t.includes("ai systems")) return {
    hook: "Engineering the Future of Data Intelligence",
    colorClass: "text-cyan-600",
    rating: "4.8",
    students: "1.4k+",
    desc1: "Build the foundational systems that power modern AI. This certification focuses on data engineering pipelines, large-scale data lakes, and the infrastructure required to feed machine learning models.",
    desc2: "Showcase your proficiency in managing complex datasets and distributed computing environments to ensure data integrity and accessibility across the enterprise.",
    skills: ["Data Lakes", "ETL Pipelines", "Apache Spark", "Data Governance", "Airflow"],
    links: [
        { label: "Data Lake Fundamentals", url: "https://www.databricks.com/discover/data-lakes?referrer=grok.com" },
        { label: "Apache Airflow Tutorial", url: "https://airflow.apache.org/docs/apache-airflow/stable/tutorial/index.html?referrer=grok.com" },
        { label: "Introduction to Apache Spark", url: "https://www.ibm.com/think/topics/apache-spark?referrer=grok.com" }
    ]
  };
  
  // Default for others
  return {
    hook: "Professional Standards in Technology",
    colorClass: "text-slate-600",
    rating: "4.7",
    students: "1k+",
    desc1: "This professional certification is designed to validate your expertise and practical skills in this specific domain of technology.",
    desc2: "Achieving this milestone demonstrates your dedication to mastering complex topics and applying them to solve real-world business problems.",
    skills: ["Industry Standards", "Practical Application", "Problem Solving", "Technical Proficiency"],
    links: []
  };
};

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // --- STATE ---
  const [activeTab, setActiveTab] = useState("dashboard"); 
  const [viewMode, setViewMode] = useState("list"); 
  const [exams, setExams] = useState([]);
  const [myAttempts, setMyAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExam, setSelectedExam] = useState(null);

  // Notifications
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);

  // Contact Form State
  const [sending, setSending] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  // Checkout State
  const [nameOnCertificate, setNameOnCertificate] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [payableAmount, setPayableAmount] = useState(0);
  const [checkoutMessage, setCheckoutMessage] = useState({ text: "", type: "" });
  const [billingInfo, setBillingInfo] = useState({ phone: "", address: "", city: "", state: "", zip: "" });
  const [showConfetti, setShowConfetti] = useState(false);

  // --- LOGIC: Fetch Data ---
  const fetchData = async () => {
    try {
      const [examsRes, attemptsRes] = await Promise.all([
        api.get("/api/exams"),
        api.get("/api/attempts/my"),
      ]);
      setExams(examsRes.data || []);
      setMyAttempts(attemptsRes.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab) setActiveTab(tab);
  }, [location.search]);

  useEffect(() => {
    if (!user || !user.email) navigate("/");
  }, [navigate, user]);

  useEffect(() => {
    fetchData();
  }, []);

  // --- CONFETTI LOADER & EFFECT ---
  useEffect(() => {
    // Inject tsparticles script
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/@tsparticles/confetti@3.0.3/tsparticles.confetti.bundle.min.js";
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  // --- UPDATED: BASIC CANNON BLAST EFFECT ---
  useEffect(() => {
    if (showConfetti && window.confetti) {
      window.confetti({
        particleCount: 150,    // Increased for a "Fuller" look
        spread: 100,           // Wider burst
        origin: { y: 0.7 },    // Centered horizontally by default (x: 0.5)
        colors: ["#F97316", "#ffffff", "#000000"], // Orivon Brand Colors
        ticks: 250,            // How long the particles stay on screen
        gravity: 1.2,          // Makes them fall realistically
        scalar: 1.1            // Makes the particles slightly larger/visible
      });
    }
  }, [showConfetti]);
  // Close notifications on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  const hasActiveAttempt = selectedExam && myAttempts.some(
    (a) =>
      a.examId &&
      (a.examId._id === selectedExam._id || a.examId === selectedExam._id) &&
      !a.revoked &&
      a.status !== "failed"
  );

  const getAttemptForExam = (examId) => {
    return myAttempts.find(a => a.examId && (a.examId._id === examId || a.examId === examId) && !a.revoked);
  };

  const handleViewExam = (exam) => {
    setSelectedExam(exam);
    setNameOnCertificate("");
    setCouponCode("");
    setCheckoutMessage({ text: "", type: "" });
    setPayableAmount(exam.price);
    setViewMode("detail");
    setActiveTab("available"); 
  };

  const handleProceedToBuy = () => {
    if (!selectedExam) return;
    setPayableAmount(selectedExam.price);
    setViewMode("checkout");
  };

  const handleBackToList = () => {
    setSelectedExam(null);
    setViewMode("list");
  };

  const handleDownloadCertificate = async (attemptId, title) => {
    try {
      const res = await api.get(`/api/certificates/${attemptId}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${title || "certificate"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download certificate");
    }
  };

  const handleApplyCoupon = () => {
    if (!selectedExam) return;
    if (couponCode.trim().toUpperCase() === "INTERNALPASS") {
      setPayableAmount(749);
      setCheckoutMessage({ text: "Exclusive members code applied!", type: "success" });
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000); 
    } else {
      setPayableAmount(selectedExam.price);
      setCheckoutMessage({ text: "Invalid coupon.", type: "error" });
    }
  };

  const startPayment = async () => {
    const phoneRegex = /^[6-9]\d{9}$/;
    
    if (
      !nameOnCertificate.trim() || 
      !billingInfo.phone.trim() || 
      !billingInfo.address.trim() || 
      !billingInfo.city.trim() || 
      !billingInfo.state.trim() || 
      !billingInfo.zip.trim()
    ) {
      setCheckoutMessage({ text: "Please fill in all required billing details to proceed.", type: "error" });
      return;
    }

    if (!phoneRegex.test(billingInfo.phone.trim())) {
      setCheckoutMessage({ text: "Please enter a valid 10-digit mobile number.", type: "error" });
      return;
    }

    const ok = await loadRazorpayScript();
    if (!ok) {
      alert("Razorpay SDK failed to load");
      return;
    }
    try {
      const orderRes = await api.post("/api/payments/create-order", {
        examId: selectedExam._id,
        nameOnCertificate,
        couponCode,
      });
      const { orderId, amount, razorpayKeyId } = orderRes.data;
      const options = {
        key: razorpayKeyId,
        amount: amount * 100,
        currency: "INR",
        name: "Orivon Technologies",
        description: "Orivon Academy Certification",
        image: "https://orivonacademy.com/logo.png",
        order_id: orderId,
        prefill: { name: user.name, email: user.email, contact: billingInfo.phone },
        handler: async function (response) {
            try {
              await api.post("/api/payments/verify", {
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              });
              setCheckoutMessage({ text: "Payment successful! Certification unlocked.", type: "success" });
              await fetchData();
              setTimeout(() => { setActiveTab("my"); setViewMode("list"); }, 1500);
            } catch (err) {
              setCheckoutMessage({ text: "Payment verification failed", type: "error" });
            }
        },
        theme: { color: "#F07E16" },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || "Could not start payment";
      setCheckoutMessage({ text: `Payment Error: ${errorMsg}`, type: "error" });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setSending(true);
    setContactSuccess(false);

    try {
      await emailjs.sendForm(
        "service_academy",        
        "template_contact",   
        e.target,
        "5paFVyWcyRQmHQEGi"     
      );
      setContactSuccess(true);
      e.target.reset();
      setTimeout(() => setContactSuccess(false), 5000); 
    } catch (err) {
      console.error(err);
      alert("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  // --- UI RENDERERS ---

  const renderDashboardHome = () => {
    const trendingExams = exams.slice(0, 4);
    const activeAttempt = myAttempts.find(a => a.status === 'in-progress' && !a.revoked);

    return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
      
      {/* 1. Scenery/Hero Section with Sales Hook */}
      <div className="relative rounded-3xl overflow-hidden shadow-2xl min-h-[280px] flex flex-col justify-end p-8 group">
         <div className="absolute inset-0 bg-gray-900"></div>
         <img src={Scene1} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-1000" alt="Scenery" />
         <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"></div>
         
         <div className="relative z-10 flex flex-col md:flex-row items-end md:items-center justify-between gap-6">
            <div className="flex items-center gap-6">
               <div className="w-20 h-20 rounded-full border border-orange-500/30 shadow-[0_0_20px_rgba(249,115,22,0.2)] overflow-hidden shrink-0 bg-orange-500/20 backdrop-blur-md flex items-center justify-center">
                  {user.picture ? (
                     <img src={user.picture} alt="User" className="w-full h-full object-cover" />
                  ) : (
                     <span className="text-3xl font-bold text-orange-100 drop-shadow-sm">
                        {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                     </span>
                  )}
               </div>
               <div className="mb-1">
                  <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-2">
                     Hello, {user.name?.split(' ')[0]}! 👋
                  </h1>
                  <p className="text-orange-100 text-lg font-medium max-w-2xl leading-relaxed">
                     Prove your skills. Get hired. <span className="text-white font-bold">Certified professionals earn 40% more</span> on average. Start today.
                  </p>
               </div>
            </div>
            
            <div className="flex gap-3">
               <div className="bg-white/10 backdrop-blur-md px-5 py-3 rounded-2xl border border-white/10 text-center">
                  <p className="text-2xl font-bold text-white">{myAttempts.length}</p>
                  <p className="text-xs text-gray-300 uppercase tracking-wider">Enrolled</p>
               </div>
               <div className="bg-orange-500/20 backdrop-blur-md border border-orange-500/20 px-5 py-3 rounded-2xl text-center">
                  <p className="text-2xl font-bold text-white">{myAttempts.filter(a => a.status === 'passed').length}</p>
                  <p className="text-xs text-orange-100 uppercase tracking-wider">Certified</p>
               </div>
            </div>
         </div>
      </div>

      <div className="space-y-8">
         {activeAttempt ? (
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between gap-6">
               <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 shrink-0">
                     <PlayCircle size={32} />
                  </div>
                  <div>
                     <h3 className="font-bold text-gray-900 text-lg">Pick up where you left off</h3>
                     <p className="text-gray-500 text-sm">Continue your <strong>{activeAttempt.examId?.title}</strong> exam.</p>
                  </div>
               </div>
               <button 
                  onClick={() => navigate(`/exam-check/${activeAttempt._id}`)}
                  className="px-6 py-2 bg-gray-900 text-white rounded-lg font-bold hover:bg-orange-600 transition-colors"
               >
                  Resume
               </button>
            </div>
         ) : (
             <div className="bg-gradient-to-r from-orange-50 to-rose-50 p-8 rounded-2xl border border-orange-100 flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                   <h3 className="font-bold text-gray-900 text-xl mb-1">Boost your career profile today</h3>
                   <p className="text-gray-600">You aren't enrolled in any active exams. Explore our catalog to find your next milestone.</p>
                </div>
                <button onClick={() => setActiveTab('available')} className="px-8 py-3 bg-white text-orange-600 shadow-sm border border-orange-100 rounded-xl font-bold hover:bg-orange-50 transition-colors shrink-0">Explore Certifications</button>
             </div>
         )}

         <div>
            <div className="flex items-center justify-between mb-6">
               <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <Zap size={20} className="text-yellow-500 fill-yellow-500"/> Trending Certifications
               </h3>
               <button onClick={() => setActiveTab('available')} className="text-sm text-orange-600 font-bold hover:underline">View Full Catalog</button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
               {trendingExams.map(exam => (
                  <div key={exam._id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col gap-4 cursor-pointer group h-full" onClick={() => handleViewExam(exam)}>
                     <div className="flex items-start justify-between">
                        <img src={getCertImage(exam.title)} alt="icon" className="w-16 h-16 rounded-xl object-cover shadow-sm" />
                        <span className="bg-gray-50 text-gray-600 text-[10px] font-bold px-2 py-1 rounded uppercase">
                            Trending
                        </span>
                     </div>
                     <div className="flex-1">
                        <h4 className="font-bold text-gray-900 text-lg leading-tight mb-2 group-hover:text-orange-600 transition-colors line-clamp-2">{exam.title}</h4>
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                            <span className="flex items-center gap-1"><FileText size={12}/> {exam.totalQuestions} Qs</span>
                            <span className="flex items-center gap-1"><Clock size={12}/> {exam.durationMinutes}m</span>
                        </div>
                     </div>
                     <div className="pt-4 border-t border-gray-50 flex items-center justify-between text-sm">
                        <span className="font-bold text-gray-900">₹{exam.price}</span>
                        <span className="text-orange-600 font-bold flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                            Details <ChevronRight size={16} />
                        </span>
                     </div>
                  </div>
               ))}
            </div>
         </div>

      </div>
    </motion.div>
    );
  };

  const renderCoursesTab = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col">
       <div className="relative w-full flex-1 min-h-[500px] bg-[#0f172a] rounded-3xl overflow-hidden flex items-center justify-center shadow-2xl border border-gray-800">
         <div className="absolute inset-0 grid grid-cols-3 gap-4 opacity-20 pointer-events-none transform scale-110 rotate-3">
             <img src={GenAIImg} className="w-full h-full object-cover rounded-xl" alt="" />
             <img src={FullStackImg} className="w-full h-full object-cover rounded-xl" alt="" />
             <img src={CloudImg} className="w-full h-full object-cover rounded-xl" alt="" />
             <img src={CyberImg} className="w-full h-full object-cover rounded-xl" alt="" />
             <img src={NoCodeImg} className="w-full h-full object-cover rounded-xl" alt="" />
             <img src={AIDataImg} className="w-full h-full object-cover rounded-xl" alt="" />
             <img src={GenAIImg} className="w-full h-full object-cover rounded-xl" alt="" />
             <img src={FullStackImg} className="w-full h-full object-cover rounded-xl" alt="" />
             <img src={CloudImg} className="w-full h-full object-cover rounded-xl" alt="" />
         </div>

         <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/80 to-transparent"></div>

         <div className="relative z-10 max-w-2xl px-6 text-center">
            <div className="inline-flex items-center justify-center p-4 bg-orange-500/10 border border-orange-500/30 rounded-full mb-6">
                <Settings size={32} className="text-orange-500 animate-spin-slow" />
            </div>
            
            <h2 className="text-4xl font-extrabold text-white mb-4 tracking-tight">
                System Maintenance In Progress
            </h2>
            
            <p className="text-lg text-gray-400 leading-relaxed mb-8">
               We are currently upgrading the <strong>Learning Hub</strong> to version 2.0. This update involves major infrastructure improvements to support interactive labs, video streaming, and real-time analytics.
            </p>

            <div className="flex flex-col md:flex-row gap-4 justify-center items-center text-sm font-medium text-gray-500">
               <span className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10">
                  <Server size={14} className="text-blue-500" /> Server Optimization
               </span>
               <span className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10">
                  <Lock size={14} className="text-green-500" /> Security Patching
               </span>
               <span className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10">
                  <Zap size={14} className="text-yellow-500" /> Content Migration
               </span>
            </div>

            <p className="mt-8 text-xs text-gray-600 uppercase tracking-widest">
               Estimated Completion: Coming Soon
            </p>
         </div>
       </div>
    </motion.div>
  );

  const renderContact = () => (
    <div className="h-[calc(100vh-140px)] flex items-center justify-center w-full">
       <div className="w-full max-w-5xl bg-white rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row h-full md:h-[600px] border border-gray-100">
          <div className="md:w-2/5 bg-[#0f172a] text-white p-8 md:p-10 flex flex-col justify-between relative overflow-hidden shrink-0">
             <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
             <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-600/20 rounded-full blur-3xl -ml-16 -mb-16 pointer-events-none"></div>
             
             <div className="relative z-10">
                <h2 className="text-3xl font-bold mb-2">Get in touch</h2>
                <p className="text-gray-400 text-sm leading-relaxed">
                   Our team is here to assist you 24/7.
                </p>
                        <div className="relative z-10 mt-10 p-4 bg-white/5 rounded-xl border border-white/10">
          <div className="flex gap-1 text-orange-500 mb-2">
            <Star size={14} fill="currentColor"/><Star size={14} fill="currentColor"/><Star size={14} fill="currentColor"/><Star size={14} fill="currentColor"/><Star size={14} fill="currentColor"/>
          </div>
          <p className="text-xs text-gray-300 italic">
            "The support team helped me resolve my payment issue instantly!"
          </p>
        </div>

             </div>

             <div className="space-y-6 relative z-10">
                <div className="flex items-start gap-4">
                   <Phone size={20} className="text-orange-500 mt-1"/>
                   <div>
                      <p className="text-xs text-gray-400 uppercase font-bold">Call us</p>
                      <p className="font-medium">+91 8169203093</p>
                   </div>
                </div>
                <div className="flex items-start gap-4">
                   <Mail size={20} className="text-orange-500 mt-1"/>
                   <div>
                      <p className="text-xs text-gray-400 uppercase font-bold">Email us</p>
                      <p className="font-medium break-all">contact@orivontechnologies.com</p>
                   </div>
                </div>
                <div className="flex items-start gap-4">
                   <MapPin size={20} className="text-orange-500 mt-1"/>
                   <div>
                      <p className="text-xs text-gray-400 uppercase font-bold">Visit us</p>
                      <p className="font-medium text-sm leading-relaxed text-gray-300">
                         Hiranandani Estate, Thane (West)<br/>
                         Maharashtra – 400607, India
                      </p>
                   </div>
                </div>
                
             </div>
          </div>

          <div className="md:w-3/5 p-8 md:p-12 flex flex-col justify-center bg-white relative">
             
             <AnimatePresence>
               {contactSuccess && (
                 <motion.div 
                   initial={{ opacity: 0, scale: 0.9 }}
                   animate={{ opacity: 1, scale: 1 }}
                   exit={{ opacity: 0 }}
                   className="absolute inset-0 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center z-20 rounded-r-3xl"
                 >
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-4">
                       <CheckCircle size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Message Sent!</h3>
                    <p className="text-gray-500 mt-2">We will get back to you shortly.</p>
                 </motion.div>
               )}
             </AnimatePresence>

             <h3 className="text-2xl font-bold text-gray-900 mb-6">Send a Message</h3>
             <form className="space-y-4" onSubmit={handleSendMessage}>
                <div className="grid grid-cols-2 gap-4">
                   <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">First Name</label>
                      <input name="first_name" required className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 outline-none focus:ring-2 focus:ring-orange-500" placeholder="John" />
                   </div>
                   <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Last Name</label>
                      <input name="last_name" required className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 outline-none focus:ring-2 focus:ring-orange-500" placeholder="Doe" />
                   </div>
                </div>
                
                <div>
                   <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label>
                   <input type="email" name="email" required className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 outline-none focus:ring-2 focus:ring-orange-500" placeholder="john@example.com" />
                </div>

                <div>
                   <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Contact Number</label>
                   <input type="tel" name="phone" required className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 outline-none focus:ring-2 focus:ring-orange-500" placeholder="+91..." />
                </div>

                <div>
                   <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Message</label>
                   <textarea rows="3" name="message" required className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 outline-none focus:ring-2 focus:ring-orange-500 resize-none" placeholder="How can we help?"></textarea>
                </div>

                <button type="submit" disabled={sending} className="bg-orange-600 text-white font-bold py-3.5 rounded-xl hover:bg-orange-700 transition-all shadow-lg shadow-orange-500/20 w-full flex items-center justify-center gap-2">
                   {sending ? "Sending..." : <>Send Message <ChevronRight size={18}/></>}
                </button>
             </form>
          </div>
       </div>
    </div>
  );

  const renderExploreList = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Explore Certifications</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {exams.map((exam) => {
          const details = getCertDetails(exam.title);
          return (
            <div key={exam._id} className="bg-white rounded-xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all border border-gray-100 overflow-hidden group flex flex-col h-full duration-300">
              
              <div className="h-48 bg-black relative overflow-hidden">
                 <img 
                   src={getCertImage(exam.title)} 
                   alt={exam.title} 
                   className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-60 group-hover:opacity-50"
                 />
                 
                 <div className="absolute top-4 left-4 flex gap-2">
                    <span className="bg-white text-black text-[10px] font-extrabold px-3 py-1 rounded-full uppercase tracking-wider shadow-md">
                        Professional
                    </span>
                 </div>
  
                 <div className="absolute top-4 right-4">
                     <span className="bg-black/80 backdrop-blur-sm text-white font-bold text-sm px-3 py-1 rounded-lg border border-white/20">
                       ₹{exam.price}
                     </span>
                 </div>
  
                 <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent"></div>
              </div>
              
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                   <div className="flex items-center text-yellow-500">
                      <Star size={12} fill="currentColor"/>
                      <span className="text-xs font-bold ml-1 text-gray-700">{details.rating}</span>
                   </div>
                   <span className="text-gray-300 text-xs">|</span>
                   <div className="flex items-center gap-1 text-gray-500 text-[11px] font-medium">
                      <Users size={12}/> {details.students} Enrolled
                   </div>
                </div>

                <h3 className="font-bold text-xl text-gray-900 mb-2 leading-tight">{exam.title}</h3>
  
                <p className="text-gray-500 text-sm mb-4 flex-1 line-clamp-3 leading-relaxed">
                  {exam.description}
                </p>
                
                <div className="flex items-center gap-4 text-xs text-gray-400 mb-6 border-t border-gray-50 pt-4">
                   <span className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded text-gray-600 font-medium"><Clock size={12}/> {exam.durationMinutes}m</span>
                   <span className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded text-gray-600 font-medium"><FileText size={12}/> {exam.totalQuestions} Questions</span>
                </div>
  
                <button 
                  onClick={() => handleViewExam(exam)}
                  className="w-full mt-auto py-3 bg-gray-900 text-white rounded-xl hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 text-sm font-bold shadow-lg"
                >
                  Enroll Now <ChevronRight size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );

  const renderMyCerts = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
       <h2 className="text-2xl font-bold text-gray-800">My Learning</h2>
       {myAttempts.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-dashed border-gray-300">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
               <BookOpen size={32}/>
            </div>
            <p className="text-gray-500 font-medium">You haven't enrolled in any certifications yet.</p>
            <button onClick={() => setActiveTab('available')} className="mt-4 text-orange-600 font-bold hover:underline">Browse Catalog</button>
          </div>
       ) : (
         <div className="grid gap-4">
           {myAttempts.map((a) => (
             <div key={a._id} className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4 hover:shadow-md transition-all">
                <div className="flex items-center gap-5 w-full md:w-auto">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 ${a.status === 'passed' ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-blue-50 text-blue-600 border border-blue-100'}`}>
                    <Award size={28} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">{a.examId?.title}</h3>
                    <div className="flex gap-3 text-sm text-gray-500 mt-1">
                       <span className={`capitalize font-bold ${a.status === 'passed' ? 'text-green-600' : 'text-blue-600'}`}>
                          {a.status}
                       </span>
                       <span>•</span>
                       <span>Joined {new Date(a.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 w-full md:w-auto mt-2 md:mt-0">
                  {!a.revoked && (a.status === "not-started" || a.status === "in-progress") && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); navigate(`/exam-check/${a._id}`); }}
                      className="flex-1 md:flex-none px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors font-bold shadow-lg shadow-blue-200"
                    >
                      <PlayCircle size={18} /> {a.status === "not-started" ? "Start Exam" : "Continue"}
                    </button>
                  )}
                  {!a.revoked && a.status === "passed" && (
                    <button onClick={() => handleDownloadCertificate(a._id, a.examId?.title)} className="flex-1 md:flex-none px-6 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 font-bold rounded-lg flex items-center justify-center gap-2 transition-colors">
                      <Download size={18} /> Download Certificate
                    </button>
                  )}
                </div>
             </div>
           ))}
         </div>
       )}
    </motion.div>
  );

  const renderExamDetails = () => {
    const details = getCertDetails(selectedExam?.title);
    
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pb-10">
        <button onClick={handleBackToList} className="flex items-center text-gray-500 hover:text-orange-600 transition-colors mb-6 font-medium">
          <ArrowLeft size={20} className="mr-2" /> Back to Certifications
        </button>
        
        <div className="relative rounded-3xl overflow-hidden shadow-2xl min-h-[400px] flex flex-col justify-end p-8 md:p-12 group">
           <div className="absolute inset-0 bg-gray-900"></div>
           <img 
             src={getCertImage(selectedExam?.title)} 
             className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:scale-105 transition-transform duration-1000" 
             alt="Header" 
           />
           <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
  
           <div className="relative z-10 max-w-4xl">

              
              <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight shadow-sm">
                {selectedExam?.title}
              </h1>
              
              <p className="text-xl font-medium leading-relaxed mb-8 max-w-2xl border-l-4 pl-4 border-gray-300 text-gray-400 italic">
                 {details.hook}
              </p>
              
              <div className="flex flex-wrap gap-4 text-sm font-medium text-white">
                 <div className="flex items-center gap-2 bg-white/10 px-4 py-3 rounded-xl border border-white/10 backdrop-blur-sm">
                    <Clock className="text-orange-400" size={20} /> 
                    <span>{selectedExam?.durationMinutes} Minutes</span>
                 </div>
                 <div className="flex items-center gap-2 bg-white/10 px-4 py-3 rounded-xl border border-white/10 backdrop-blur-sm">
                    <FileText className="text-blue-400" size={20} /> 
                    <span>{selectedExam?.totalQuestions} Questions</span>
                 </div>
                 <div className="flex items-center gap-2 bg-white/10 px-4 py-3 rounded-xl border border-white/10 backdrop-blur-sm">
                    <Globe className="text-green-400" size={20} /> 
                    <span>English</span>
                 </div>
              </div>
              
           </div>
        </div>
  
        <div className="flex flex-col lg:flex-row gap-8 mt-10">
           <div className="flex-1 space-y-8">
              <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
                 <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <BookOpen className="text-blue-600" size={24}/> About This Certification
                 </h3>
                 <div className="space-y-4 text-gray-600 leading-relaxed text-lg">
                    <p>{details.desc1}</p>
                    <p>{details.desc2}</p>
                 </div>
              </div>

              {details.links && details.links.length > 0 && (
                <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
                   <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <Layers className="text-blue-500" size={24}/> Preparation Resources
                   </h3>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {details.links.map((link, idx) => (
                        <a 
                          key={idx} 
                          href={link.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-4 bg-gray-50 hover:bg-orange-50 rounded-xl border border-gray-100 hover:border-orange-200 transition-all group"
                        >
                           <span className="font-bold text-gray-700 group-hover:text-orange-700 text-sm">{link.label}</span>
                           <ExternalLink size={16} className="text-gray-400 group-hover:text-orange-500" />
                        </a>
                      ))}
                   </div>
                </div>
              )}
              
              <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
                 <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Zap className="text-orange-500 fill-orange-500" size={24}/> Skills You Will Gain
                 </h3>
                 <div className="flex flex-wrap gap-3">
                    {details.skills.map((skill, i) => (
                      <span key={i} className="px-5 py-2.5 bg-gray-50 text-gray-800 rounded-lg border border-gray-200 font-bold text-sm hover:bg-gray-100 transition-colors">
                        {skill}
                      </span>
                    ))}
                 </div>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 rounded-2xl border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                     <ShieldCheck className="text-gray-700" size={20} /> What to expect
                  </h3>
                  <ul className="space-y-3">
                     <li className="flex items-start gap-3 text-gray-600 text-sm">
                        <CheckCircle size={18} className="text-green-500 shrink-0 mt-0.5"/>
                        Multiple choice and scenario-based questions.
                     </li>
                     <li className="flex items-start gap-3 text-gray-600 text-sm">
                        <CheckCircle size={18} className="text-green-500 shrink-0 mt-0.5"/>
                        Instant results and performance analysis.
                     </li>
                     <li className="flex items-start gap-3 text-gray-600 text-sm">
                        <CheckCircle size={18} className="text-green-500 shrink-0 mt-0.5"/>
                        Digital badge and verifiable PDF certificate upon passing.
                     </li>
                  </ul>
              </div>
           </div>
           
           <div className="lg:w-96 shrink-0">
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-xl sticky top-24">
                 <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to certify?</h3>
                 <p className="text-sm text-gray-500 mb-6">Join thousands of certified professionals.</p>
                 {hasActiveAttempt ? (
                   <div className="bg-green-50 p-6 rounded-xl border border-green-200 text-center">
                     <CheckCircle size={40} className="text-green-600 mx-auto mb-3" />
                     <p className="font-bold text-green-800 text-lg">You are enrolled!</p>
                     <p className="text-green-600 text-sm mb-4">Good luck with your exam.</p>
                     <button onClick={() => navigate('/dashboard?tab=my')} className="w-full py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-all shadow-lg shadow-green-200">
                       Go to My Learning
                     </button>
                   </div>
                 ) : (
                   <>
                     <div className="flex items-end gap-2 mb-6">
                       <span className="text-5xl font-extrabold text-gray-900">₹{selectedExam?.price}</span>
                       <span className="text-gray-400 text-lg mb-1 line-through font-medium">₹{Math.round(selectedExam?.price * 1.2)}</span>
                     </div>
                     <button onClick={handleProceedToBuy} className="w-full py-4 bg-orange-600 text-white rounded-xl font-bold text-lg hover:bg-orange-700 transition-all shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2">
                       Register for Exam <ArrowRight size={20}/>
                     </button>
                     <div className="mt-8 space-y-4 text-sm text-gray-500">
                        <div className="flex justify-between border-b border-gray-100 pb-3">
                           <span>Format</span>
                           <span className="font-bold text-gray-900">Online & Proctored</span>
                        </div>
                        <div className="flex justify-between border-b border-gray-100 pb-3">
                           <span>Passing Score</span>
                           <span className="font-bold text-green-600">{selectedExam?.passScore}%</span>
                        </div>
                        <div className="flex justify-between">
                           <span>Validity</span>
                           <span className="font-bold text-gray-900">Lifetime</span>
                        </div>
                     </div>
                     <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-xs text-gray-400">
                        <ShieldCheck size={14}/> Secure payment via Razorpay
                     </div>
                   </>
                 )}
              </div>
           </div>
        </div>
      </motion.div>
    );
  };

  const renderCheckout = () => (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="max-w-5xl mx-auto relative">
      <div className="mb-4 flex flex-col items-center">
        <button onClick={() => setViewMode("detail")} className="flex items-center text-gray-500 hover:text-orange-600 transition-colors font-medium self-start"><ArrowLeft size={20} className="mr-2" /> Cancel & Go Back</button>
        <p style={{ fontSize: "14px" }} className="text-gray-400 mt-3 mb-1 italic font-medium text-center max-w-2xl leading-tight">
          You're just one step away from starting your professional career journey.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-10">
         <div className="flex-1">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
               <div className="mb-6">
                 <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><CreditCard className="text-orange-500"/> Billing Details</h2>
                 <p className="text-xs text-gray-500 mt-1">Finalize your details to claim your industry-recognized certification.</p>
               </div>
               
               <div className="space-y-5">
                  <div><label className="block text-sm font-bold text-gray-700 mb-2">Name on Certificate <span className="text-red-500">*</span></label><input type="text" required value={nameOnCertificate} onChange={(e) => setNameOnCertificate(e.target.value)} className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-orange-500 outline-none transition-all bg-gray-50 focus:bg-white" placeholder="Enter your full legal name" /><p className="text-xs text-gray-500 mt-1">This name will appear on your official certificate.</p></div>
                  <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-bold text-gray-700 mb-2">Email Address</label><input type="email" value={user.email} disabled className="w-full border border-gray-200 bg-gray-100 text-gray-500 rounded-lg p-3 cursor-not-allowed" /></div><div><label className="block text-sm font-bold text-gray-700 mb-2">Phone Number <span className="text-red-500">*</span></label><input type="text" required value={billingInfo.phone} onChange={(e) => setBillingInfo({...billingInfo, phone: e.target.value})} className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-orange-500 outline-none" placeholder="+91..." /></div></div>
                  <div><label className="block text-sm font-bold text-gray-700 mb-2">Billing Address <span className="text-red-500">*</span></label><input type="text" required value={billingInfo.address} onChange={(e) => setBillingInfo({...billingInfo, address: e.target.value})} className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-orange-500 outline-none mb-3" placeholder="Street Address" /><div className="grid grid-cols-3 gap-3"><input type="text" required value={billingInfo.city} onChange={(e) => setBillingInfo({...billingInfo, city: e.target.value})} className="border border-gray-300 rounded-lg p-3 outline-none" placeholder="City" /><input type="text" required value={billingInfo.state} onChange={(e) => setBillingInfo({...billingInfo, state: e.target.value})} className="border border-gray-300 rounded-lg p-3 outline-none" placeholder="State" /><input type="text" required value={billingInfo.zip} onChange={(e) => setBillingInfo({...billingInfo, zip: e.target.value})} className="border border-gray-300 rounded-lg p-3 outline-none" placeholder="Zip Code" /></div></div>
               </div>
            </div>

         </div>
         <div className="lg:w-96 shrink-0">
            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 sticky top-24">
               <h3 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h3>
               <div className="flex gap-3 mb-6 pb-6 border-b border-gray-200"><img src={getCertImage(selectedExam?.title)} className="w-16 h-16 rounded-lg object-cover" alt="Thumb"/><div><p className="font-bold text-sm text-gray-900 line-clamp-2">{selectedExam?.title}</p><p className="text-xs text-gray-500 mt-1">Professional Certification</p></div></div>
               <div className="space-y-3 mb-6"><div className="flex justify-between text-sm text-gray-600"><span>Subtotal</span><span>₹{payableAmount}</span></div><div className="flex justify-between text-sm text-gray-600"><span>Processing Fee</span><span>₹0.00</span></div><div className="flex justify-between text-sm text-gray-600"><span>Tax (18% GST)</span><span>₹{(payableAmount * 0.18).toFixed(2)} (Incl)</span></div><div className="pt-4 mt-4 border-t border-gray-200"><div className="flex gap-2"><input type="text" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} className="flex-1 border border-gray-300 rounded-lg p-2 text-sm uppercase outline-none" placeholder="Coupon Code" /><button onClick={handleApplyCoupon} className="text-xs font-bold text-orange-600 hover:bg-orange-50 px-3 rounded-lg border border-orange-200">APPLY</button></div>{checkoutMessage.text && (<p className={`text-xs mt-2 ${checkoutMessage.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>{checkoutMessage.text}</p>)}</div></div>
               <div className="flex justify-between items-center border-t border-gray-200 pt-4 mb-6"><span className="font-bold text-lg text-gray-900">Total</span><span className="font-bold text-2xl text-orange-600">₹{payableAmount}</span></div>
               <button onClick={startPayment} className="w-full py-3.5 bg-gray-900 text-white rounded-xl font-bold shadow-lg hover:bg-gray-800 transition-all flex items-center justify-center gap-2">Complete Payment <ArrowRight size={18}/></button>
               <p className="text-xs text-gray-400 text-center mt-4">By completing your purchase you agree to these <span className="underline cursor-pointer">Terms of Service</span>.</p>
            </div>
         </div>
      </div>
    </motion.div>
  );

  const getHeaderTitle = () => {
     if (activeTab === 'available' && viewMode !== 'list') return 'Certification Details';
     
     const titles = {
        'dashboard': 'Dashboard',
        'courses': 'Learning Hub',
        'available': 'Explore Certifications',
        'my': 'My Learning',
        'contact': 'Support'
     };
     return titles[activeTab] || 'Dashboard';
  };

  return (
    <div className="flex h-screen w-screen bg-gray-50 overflow-hidden font-sans">
      
      {/* SIDEBAR */}
      <aside className="w-64 bg-[#0f172a] text-gray-400 flex flex-col justify-between shrink-0 z-20">
        <div>
          <div className="h-20 flex items-center px-6 border-b border-gray-800/50">
             <img src={Logo} alt="Orivon" className="h-8 w-auto mr-3" />
             <span className="text-lg font-bold tracking-tight text-white whitespace-nowrap">ORIVON ACADEMY</span>
          </div>
          <nav className="p-4 space-y-2 mt-4">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
              { id: 'courses', label: 'Learning Hub', icon: BookOpen },
              { id: 'available', label: 'Explore', icon: Compass },
              { id: 'my', label: 'My Certifications', icon: Award },
              { id: 'contact', label: 'Contact Support', icon: Mail },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setViewMode("list"); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 font-medium ${
                  activeTab === item.id 
                    ? "bg-orange-600 text-white shadow-lg shadow-orange-900/30" 
                    : "hover:bg-gray-800 hover:text-white"
                }`}
              >
                <item.icon size={20} className={activeTab === item.id ? "text-white" : "text-gray-400 group-hover:text-white"} />
                {item.label}
              </button>
            ))}
          </nav>
        </div>
        <div className="p-4 border-t border-gray-800/50">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-500/10 text-gray-400 hover:text-red-400 transition-colors">
            <LogOut size={20} /> Logout
          </button>
        </div>
      </aside>

      {/* CONTENT AREA */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden relative bg-[#f8fafc]">
        {/* HEADER */}
        <header className="h-20 bg-white/80 backdrop-blur-md border-b border-gray-200 flex items-center justify-between px-8 sticky top-0 z-50">
          <div>
             <h2 className="font-bold text-2xl text-gray-800 capitalize">
                {getHeaderTitle()}
             </h2>
             <p className="text-xs text-gray-400 mt-1">Manage your learning journey</p>
          </div>
          
          <div className="flex items-center gap-6">
             {/* Notification Bell */}
             <div className="relative" ref={notifRef}>
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2 text-gray-400 hover:bg-gray-100 rounded-full transition-colors relative"
                >
                   <Bell size={22} />
                </button>
                
                <AnimatePresence>
                  {showNotifications && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 p-4 z-50"
                    >
                       <div className="flex justify-between items-center mb-2">
                          <h4 className="text-sm font-bold text-gray-800">Notifications</h4>
                          <button onClick={() => setShowNotifications(false)}><X size={14} className="text-gray-400"/></button>
                       </div>
                       <div className="py-8 text-center">
                          <div className="w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-2 text-gray-400">
                             <Bell size={18} />
                          </div>
                          <p className="text-xs text-gray-500">No new notifications</p>
                       </div>
                    </motion.div>
                  )}
                </AnimatePresence>
             </div>

             <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-full pl-2 pr-4 py-1.5 shadow-sm">
                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-100">
                    {user.picture ? (
                       <img src={user.picture} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                       <div className="w-full h-full bg-gradient-to-tr from-orange-400 to-rose-500 text-white flex items-center justify-center font-bold text-sm">
                          {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                       </div>
                    )}
                </div>
                <div className="flex flex-col">
                   <span className="text-xs font-bold text-gray-800 leading-tight">{user.name}</span>
                   <span className="text-[10px] text-gray-400 leading-tight">Student</span>
                </div>
             </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-8 scroll-smooth">
          <div className="max-w-7xl mx-auto">
             {loading ? (
                <div className="flex flex-col items-center justify-center h-96">
                   <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
                   <p className="text-gray-400 mt-4 text-sm font-medium">Loading Experience...</p>
                </div>
             ) : (
               <>
                 {activeTab === 'dashboard' && renderDashboardHome()}
                 {activeTab === 'courses' && renderCoursesTab()} 
                 {activeTab === 'available' && (
                    <>
                        {viewMode === 'list' && renderExploreList()}
                        {viewMode === 'detail' && renderExamDetails()}
                        {viewMode === 'checkout' && renderCheckout()}
                    </>
                 )}
                 {activeTab === 'my' && renderMyCerts()}
                 {activeTab === 'contact' && renderContact()}
               </>
             )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
